import { createSlice, PayloadAction, isPending, isRejected } from '@reduxjs/toolkit'
import { GetKongLocationAllResponseType } from 'features/Location/@types/location'
import { GetConversionListResponseType } from 'features/Product/services/conversionRule'
import {
  ConversionTaskItemType,
  ConversionTaskDetailType,
  GetConversionTaskParamsType,
  GetPicListResponseType,
} from 'features/Product/services/conversionTask'
import {
  fetchConversionTaskList,
  fetchGetLocations,
  cancelConversionTaskItem,
  fetchConversionTaskDetail,
  assignReassignConversionTask,
  completeConversionTask,
  getPicConversionTask,
  fetchManufacturingRuleList,
  createConversionTask,
} from './thunk'

export type ConversionTaskStatusType = 'CREATED' | 'ASSIGNED' | 'COMPLETED' | 'CANCELLED'

export type ConversionTaskStatusFilterType = ConversionTaskStatusType | 'ALL'

export type ConversionTaskLocationOptionInterface = {
  id: string
  name: string
}

export type ConversionTaskInitialStateType = {
  isLoading: boolean
  queryParams: GetConversionTaskParamsType
  conversionTaskList: ConversionTaskItemType[]
  selectedLocation: GetKongLocationAllResponseType['location'][number] | null
  constants: {
    locations: GetKongLocationAllResponseType['location']
  }
  modalAddTask: {
    isOpen: boolean
    isLoadingRuleList: boolean
    manufacturingRuleList: GetConversionListResponseType[]
    resultRulesByProductDestination: GetConversionListResponseType[]
    locationList: GetKongLocationAllResponseType['location']
    isSubmittingData: boolean
    referenceList: string[]
  }
  modalCancelTask: {
    isOpen: boolean
    id: Nullable<number>
    orderNumber: Nullable<string>
  }
  modalAssignPic: {
    isOpen: boolean
    listPic: GetPicListResponseType[]
    staffId: number
  }
  modalConfirmationComplete: {
    isOpen: boolean
  }
  conversionTaskDetail: Nullable<ConversionTaskDetailType>
}

export const initialState: ConversionTaskInitialStateType = {
  isLoading: false,
  queryParams: {
    page_index: 1,
    page_size: 20,
    order_number: '',
    status: 'ALL',
  },
  conversionTaskList: [],
  selectedLocation: null,
  constants: {
    locations: [],
  },
  modalAddTask: {
    isOpen: false,
    isLoadingRuleList: false,
    manufacturingRuleList: [],
    resultRulesByProductDestination: [],
    locationList: [],
    isSubmittingData: false,
    referenceList: [],
  },
  modalCancelTask: {
    isOpen: false,
    id: null,
    orderNumber: null,
  },
  modalAssignPic: {
    isOpen: false,
    listPic: [],
    staffId: 0,
  },
  modalConfirmationComplete: {
    isOpen: false,
  },
  conversionTaskDetail: null,
}

const resetModalCancelTaskState = (state: ConversionTaskInitialStateType) => {
  state.modalCancelTask = initialState.modalCancelTask
}

const resetModalAssignPicState = (state: ConversionTaskInitialStateType) => {
  state.modalAssignPic = initialState.modalAssignPic
}

const conversionTaskSlice = createSlice({
  name: 'conversionTask',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setQueryParams: (state, action: PayloadAction<Partial<GetConversionTaskParamsType>>) => {
      state.queryParams = { ...state.queryParams, ...action.payload }
    },
    setSelectedLocation: (
      state,
      action: PayloadAction<ConversionTaskInitialStateType['selectedLocation']>,
    ) => {
      state.selectedLocation = action.payload
    },
    setModalAddTask: (
      state,
      action: PayloadAction<Partial<ConversionTaskInitialStateType['modalAddTask']>>,
    ) => {
      state.modalAddTask = {
        ...state.modalAddTask,
        ...action.payload,
      }
    },
    resetModalAddTask: (state) => {
      state.modalAddTask = initialState.modalAddTask
    },
    setModalCancelTask: (
      state,
      action: PayloadAction<Partial<ConversionTaskInitialStateType['modalCancelTask']>>,
    ) => {
      state.modalCancelTask = {
        ...state.modalCancelTask,
        ...action.payload,
      }
    },
    setModalAssignPic: (
      state,
      action: PayloadAction<Partial<ConversionTaskInitialStateType['modalAssignPic']>>,
    ) => {
      state.modalAssignPic = {
        ...state.modalAssignPic,
        ...action.payload,
      }
    },
    setModalConfirmationComplete: (
      state,
      action: PayloadAction<Partial<ConversionTaskInitialStateType['modalConfirmationComplete']>>,
    ) => {
      state.modalConfirmationComplete = {
        ...state.modalConfirmationComplete,
        ...action.payload,
      }
    },
    resetModalCancelTask: resetModalCancelTaskState,
    resetModalAssignPic: resetModalAssignPicState,
    resetConversionTask: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversionTaskList.fulfilled, (state, action) => {
        state.isLoading = false
        state.conversionTaskList = action.payload
      })
      .addCase(fetchGetLocations.fulfilled, (state, action) => {
        state.constants.locations = action.payload
      })
      .addCase(cancelConversionTaskItem.fulfilled, resetModalCancelTaskState)
      .addCase(fetchConversionTaskDetail.fulfilled, (state, action) => {
        state.isLoading = false
        state.conversionTaskDetail = action.payload
      })
      .addCase(getPicConversionTask.fulfilled, (state, action) => {
        state.modalAssignPic.listPic = action.payload
      })
      .addCase(assignReassignConversionTask.fulfilled, resetModalAssignPicState)
      .addCase(completeConversionTask.fulfilled, (state) => {
        state.modalConfirmationComplete = initialState.modalConfirmationComplete
      })
      .addCase(fetchManufacturingRuleList.fulfilled, (state, action) => {
        state.modalAddTask[action.meta.arg.type] = action.payload
        state.modalAddTask.referenceList = action.payload.map((item) => item.rule_name)
        state.modalAddTask.isLoadingRuleList = false
      })
      .addCase(createConversionTask.fulfilled, (state) => {
        state.modalAddTask = initialState.modalAddTask
      })
      .addMatcher(isPending(createConversionTask), (state) => {
        state.modalAddTask.isSubmittingData = true
      })
      .addMatcher(isRejected(createConversionTask), (state) => {
        state.modalAddTask.isSubmittingData = false
      })
      .addMatcher(isPending(fetchConversionTaskList, fetchConversionTaskDetail), (state) => {
        state.isLoading = true
      })
      .addMatcher(isRejected(fetchConversionTaskList, fetchConversionTaskDetail), (state) => {
        state.isLoading = false
      })
      .addMatcher(isPending(fetchManufacturingRuleList), (state) => {
        state.modalAddTask.isLoadingRuleList = true
      })
      .addMatcher(isRejected(fetchManufacturingRuleList), (state) => {
        state.modalAddTask.isLoadingRuleList = false
      })
  },
})

export const {
  setIsLoading,
  setQueryParams,
  setSelectedLocation,
  setModalAddTask,
  resetModalAddTask,
  setModalCancelTask,
  setModalAssignPic,
  setModalConfirmationComplete,
  resetModalCancelTask,
  resetModalAssignPic,
  resetConversionTask,
} = conversionTaskSlice.actions

export default conversionTaskSlice.reducer
